import { type VFC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { toSafeInt } from "widgets/utils";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import ProteinGenomeView from "./ProteinGenomeView.js";
import ProteinRelatedProteinFeaturesView from "./ProteinRelatedProteinFeaturesView.js";
import ProteinSummaryView from "./ProteinSummaryView.js";
import ProteinVariantsView from "./ProteinVariantsView.js";
import { isGenomeViewerEnabledEntity } from "@/app-utils/entity.ts";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { type IProteinState, shouldLoadProtein } from "@/redux/modules/protein.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";
import { ProteinRelatedOrthologsView } from "@/ui-modules/entities/ProteinRelatedOrthologsView.tsx";

/**
 * The ProteinPage smart component is called from the route corresponding to protein/:proteinId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. protein/:proteinId/summary)
 * The ProteinPage will take care of filling up the basic protein data and the nested child pages may request additional
 * data that is needed to show the nested page (such as transcript and protein child collections for the summary page)
 */
const ProteinDetailPage: VFC = () => {
  const { proteinId } = useParams<"proteinId">();
  const { protein, isLoading } = useSelector<IAppState, IProteinState>((state) => state.protein);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadProtein, proteinId);

  useEffect(() => {
    void load();
  }, [load]);

  const getNavItems = (): IRouteNavItemProps[] => {
    const variantsDec = {
      disabled: isLoading || (protein && !protein.hasVariants),
      tooltip: "There are no variants registered for this protein",
    };
    const orthologsDec = {
      disabled: isLoading || (protein && !protein.orthologTypes.length),
      tooltip: "There are no orthologs registered for this protein",
    };
    // enable genome viewer if there is at least one fitting location
    const viewableLocationAbsent = !protein?.proteinLocations
      .map((p) => p.region.contig)
      .find((c) => isGenomeViewerEnabledEntity(c.organism));
    const proteinLocationsMissing: boolean = protein && protein.proteinLocations.length === 0;
    const genomeviewDisabled = isLoading || proteinLocationsMissing || viewableLocationAbsent;
    const genomeviewDec = {
      disabled: genomeviewDisabled,
      tooltip: proteinLocationsMissing
        ? "Contig location has not been imported or calculated for this protein"
        : viewableLocationAbsent
          ? "Protein is not located on a genomic contig"
          : undefined,
    };

    const proteinFeaturesDec = {
      disabled: protein && protein.featureStatsData?.length === 0,
      tooltip:
        protein && protein.featureStatsData?.length === 0
          ? "Features have not been calculated for this protein"
          : undefined,
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <ProteinSummaryView />,
        default: true,
        isLoading,
      },
      {
        label: "Orthologs",
        disabled: orthologsDec.disabled,
        disabledTooltip: orthologsDec.tooltip,
        route: "orthologs",
        pageComponent: <ProteinRelatedOrthologsView />,
        isLoading,
      },
      {
        label: "Protein Features",
        disabled: proteinFeaturesDec.disabled,
        disabledTooltip: proteinFeaturesDec.tooltip,
        route: "features",
        pageComponent: <ProteinRelatedProteinFeaturesView />,
        isLoading,
      },
      {
        label: "Variants",
        disabled: variantsDec.disabled,
        disabledTooltip: variantsDec.tooltip,
        route: "variants",
        pageComponent: <ProteinVariantsView />,
        isLoading,
      },
      {
        label: "Genome View",
        disabled: genomeviewDec.disabled,
        disabledTooltip: genomeviewDec.tooltip,
        route: "genome",
        pageComponent: <ProteinGenomeView />,
        isLoading,
      },
    ];
  };
  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.PROTEIN}
      entityId={toSafeInt(proteinId)}
      getDisplayName={() => protein?.sequenceId}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export default ProteinDetailPage;
